<template>
  <!-- eslint-disable -->
  <!--
    SweetModal for Vue.js
    Sweet, easy and powerful modals and dialogs

    Copyright (c) 2017 Adepto.as AS · Oslo, Norway
    Dual licensed under the MIT and GPL licenses.

    See LICENSE-MIT.txt and LICENSE-GPL.txt
  -->
  <div :class="overlay_classes" v-show="is_open" v-on:click="_onOverlayClick">
    <div :class="modal_classes" :style="modal_style">
      <div class="sweet-box-actions">
        <!-- Custom Actions -->
        <slot name="box-action"></slot>

        <!-- Close Button -->
        <div class="sweet-action-close" v-on:click="close" v-if="!hideCloseButton">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="#292c34" /></svg>
        </div>
      </div>

      <div class="sweet-title" v-if="has_title">
        <!-- Title -->
        <h2 v-if="title" v-html="title"></h2>
        <slot name="title"></slot>
      </div>

      <!-- Content: Wrapper -->
      <div class="sweet-content" ref="content">
        <!-- Icon: Error -->
        <div class="sweet-modal-icon sweet-modal-error" v-if="icon == 'error'" ref="icon_error">
          <span class="sweet-modal-x-mark">
            <span class="sweet-modal-line sweet-modal-left"></span>
            <span class="sweet-modal-line sweet-modal-right"></span>
          </span>
        </div>

        <!-- Icon: Warning -->
        <div class="sweet-modal-icon sweet-modal-warning" v-if="icon == 'warning'" ref="icon_warning">
          <span class="sweet-modal-body"></span>
          <span class="sweet-modal-dot"></span>
        </div>

        <!-- Icon: Info -->
        <div class="sweet-modal-icon sweet-modal-info" v-if="icon == 'info'" ref="icon_info"></div>

        <!-- Icon: Success -->
        <div class="sweet-modal-icon sweet-modal-success" v-if="icon == 'success'" ref="icon_success">
          <span class="sweet-modal-line sweet-modal-tip"></span>
          <span class="sweet-modal-line sweet-modal-long"></span>
          <div class="sweet-modal-placeholder"></div>
          <div class="sweet-modal-fix"></div>
        </div>

        <!-- Actual Content -->
        <div class="sweet-content-content" v-if="$slots.default">
          <slot></slot>
        </div>
      </div>

      <!-- Buttons -->
      <div class="sweet-buttons" v-if="$slots.button">
        <slot name="button"></slot>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
  export default {
    name: 'SweetModal',

    emits: [
      'open',
      'close',
    ],

    props: {
      title: {
        type: String,
        required: false,
        default: ''
      },

      overlayTheme: {
        type: String,
        required: false,
        default: 'light'
      },

      modalTheme: {
        type: String,
        required: false,
        default: 'light'
      },

      blocking: {
        type: Boolean,
        required: false,
        default: false
      },

      pulseOnBlock: {
        type: Boolean,
        required: false,
        default: true
      },

      icon: {
        type: String,
        required: false,
        default: ''
      },

      hideCloseButton: {
        type: Boolean,
        required: false,
        default: false
      },

      enableMobileFullscreen: {
        type: Boolean,
        required: false,
        default: true
      },

      width: {
        type: [Number, String],
        required: false,
        default: null
      }
    },

    mounted() {
      document.addEventListener('keyup', this._onDocumentKeyup)
    },

    beforeUnmount() {
      this._unlockBody()
      document.removeEventListener('keyup', this._onDocumentKeyup)
    },

    data() {
      return {
        visible: false,
        is_open: false,
        is_bouncing: false,

        backups: {
          body: {
            height: null,
            overflow: null
          }
        }
      }
    },

    computed: {
      has_title() {
        return this.title || this.$slots.title
      },

      has_content() {
        return this.$slots.default
      },

      overlay_classes() {
        return [
          'sweet-modal-overlay',
          'theme-' + this.overlayTheme,
          'sweet-modal-clickable',
          {
            'is-visible': this.visible,
            blocking: this.blocking
          }
        ]
      },

      modal_classes() {
        return [
          'sweet-modal',
          'theme-' + this.modalTheme,
          {
            'has-title': this.has_title,
            'has-content': this.has_content,
            'has-icon': this.icon,
            'is-mobile-fullscreen': this.enableMobileFullscreen,
            'is-visible': this.visible,
            'is-alert': !this.icon && !this.title && !this.$slots.title,
            bounce: this.is_bouncing,
          }
        ]
      },

      modal_style() {
        let width = this.width
        let maxWidth = null

        if (width !== null) {
          if (Number(width) == width) {
            width = width + 'px'
          }

          maxWidth = 'none'
        }

        return {
          width,
          maxWidth
        }
      }
    },

    methods: {
      /**
       * Open the dialog
       * Emits an event 'open'
       *
       * @param tabId string     Optional id or index of initial tab element.
       */
      open() {
        this.is_open = true
        this._lockBody()
        this._animateIcon()

        setTimeout(() => this.visible = true, 30)
        this.$emit('open')
      },

      /**
       * Close the dialog
       * Emits an event 'close'
       */
      close() {
        this.visible = false
        this._unlockBody()

        setTimeout(() => this.is_open = false, 300)
        this.$emit('close')
      },

      /**
       * Bounce the modal.
       */
      bounce() {
        this.is_bouncing = true

        setTimeout(() => this.is_bouncing = false, 330)
      },

      /**********************
          INTERNAL METHODS
       **********************/

      _lockBody() {
        if (this.bodyLocked) {
          return
        }
        this.bodyLocked = true

        this.backups.body.height = document.body.style.height
        this.backups.body.overflow = document.body.style.overflow

        document.body.style.height = '100%'
        document.body.style.overflow = 'hidden'
      },

      _unlockBody() {
        if (!this.bodyLocked) {
          return
        }
        this.bodyLocked = false

        document.body.style.height = this.backups.body.height
        document.body.style.overflow = this.backups.body.overflow
      },

      _onOverlayClick(event) {
        if (!event.target.classList || event.target.classList.contains('sweet-modal-clickable')) {
          if (this.blocking) {
            if (this.pulseOnBlock) this.bounce()
          } else {
            this.close()
          }
        }
      },

      _onDocumentKeyup(event) {
        if (event.keyCode == 27) {
          if (this.blocking) {
            if (this.pulseOnBlock) this.bounce()
          } else {
            this.close()
          }
        }
      },

      _animateIcon() {
        if (!this.icon) return

        switch (this.icon) {
          case 'success':
            setTimeout(() => {
              this._applyClasses(this.$refs.icon_success, {
                '': [ 'animate' ],
                '.sweet-modal-tip': [ 'animateSuccessTip' ],
                '.sweet-modal-long': [ 'animateSuccessLong' ]
              })
            }, 80)

            break;

          case 'warning':
            this._applyClasses(this.$refs.icon_warning, {
              '': [ 'pulseWarning' ],
              '.sweet-modal-body': [ 'pulseWarningIns' ],
              '.sweet-modal-dot': [ 'pulseWarningIns' ]
            })

            break;

          case 'error':
            setTimeout(() => {
              this._applyClasses(this.$refs.icon_error, {
                '': [ 'animateErrorIcon' ],
                '.sweet-modal-x-mark': [ 'animateXMark' ]
              })
            }, 80)

            break;
        }
      },

      /**
       * Apply classes from the classMap to $ref or children of $ref, a native
       * DOMElement.
       *
       * ClassMap:
       * {
       *     'selector': [ 'class1', 'class2', ... ]
       * }
       *
       * Empty Selector selects $ref.
       *
       * @param DOMNode $ref     Element to apply classes to or children of that element
       * @param Object  classMap Class Map which elements get which classes (see doc)
       */
      _applyClasses($ref, classMap) {
        for (let cl in classMap) {
          let classes = classMap[cl]
          let $el

          if (cl == '') {
            $el = $ref
          } else {
            $el = $ref.querySelector(cl)
          }

          $el.classList.remove(...classes)
          $el.classList.add(...classes)
        }
      }
    }
  }
</script>

<style lang="scss">
  // stylelint-disable
  @use "sass:color";
  @use './mixins';
  @use './colors';
  @use './animations';
  @use './icons';

  .sweet-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;

    width: 100vw;
    height: 100%;

    z-index: 9001;

    font-size: 14px;

    -webkit-font-smoothing: antialiased;

    // Theming
    background: rgba(#fff, 0.9);

    &.theme-dark {
      $color: colors.color(dark-overlay);

      // background: radial-gradient(ellipse at center, rgba($color, 0.9) 0%, rgba($color, 0.96) 100%);
      background: rgba($color, 0.94);
    }

    // Animation
    & {
      opacity: 0;
      transition: opacity 0.3s;
      transform: translate3D(0, 0, 0);
      -webkit-perspective: 500px;
    }

    &.is-visible {
      opacity: 1;
    }
  }

  .sweet-modal {
    @include mixins.border-box;

    background: #fff;
    box-shadow: 0px 8px 46px rgba(#000, 0.08),
          0px 2px  6px rgba(#000, 0.03);

    position: absolute;
    top: 50%;
    left: 50%;
    // transform: translate(-50%, -50%); // Done by the animation later

    width: 80%;
    max-width: 640px;
    max-height: 100vh;
    overflow-y: auto;

    border-radius: 2px;

    .sweet-box-actions {
      position: absolute;
      top: 12px;
      right: 12px;

      .sweet-action-close {
        display: inline-block;
        cursor: pointer;

        color: colors.color(dark);
        text-align: center;

        width: 42px;
        height: 42px;
        line-height: 42px;

        border-radius: 50%;

        svg {
          width: 24px;
          height: 24px;

          vertical-align: middle;
          margin-top: -2px;

          @include mixins.svg-path(true) {
            fill: currentColor;
          }
        }

        &:hover {
          background: colors.color(blue);
          color: #fff;
        }
      }
    }

    .sweet-title {
      @include mixins.ellipsis;

      height: 64px;
      line-height: 64px;

      border-bottom: 1px solid colors.color(border);

      padding: {
        left: 32px;
        right: 64px;
      }

      > h2 {
        @include mixins.ellipsis;
        @include mixins.mp0;

        font-weight: 500;
        font-size: 22px;
      }
    }

    .sweet-content {
      display: flex;
      align-items: center;

      padding: {
        left: 32px;
        right: 32px;
        top: 24px;
        bottom: 24px;
      }

      line-height: 1.5;

      .sweet-content-content {
        flex-grow: 1;
      }

      .sweet-modal-tab:not(.active) {
        display: none;
      }

      .sweet-modal-icon {
        margin-bottom: 36px;
      }
    }

    .sweet-buttons {
      text-align: right;

      padding: {
        left: 20px;
        right: 20px;
        top: 12px;
        bottom: 12px;
      }
    }

    .sweet-content + .sweet-buttons {
      border-top: 1px solid colors.color(border);
    }

    // Special Styles
    &.is-alert {

      .sweet-content {
        display: block;

        text-align: center;
        font-size: 16px;

        padding: {
          top: 64px;
          bottom: 64px;
        }
      }
    }

    &:not(.has-content) {

      .sweet-modal-icon {
        margin-bottom: 0;
      }
    }

    // Theming
    &.theme-dark {
      $color: colors.color(dark-modal);

      background: $color;
      color: #fff;

      .sweet-box-actions {

        .sweet-action-close {
          color: #fff;
        }
      }

      .sweet-title {
        border-bottom-color: color.scale($color, $lightness: -8%);
        box-shadow: 0px 1px 0px color.scale($color, $lightness: 8%);
      }

      .sweet-content + .sweet-buttons {
        border-top-color: color.scale($color, $lightness: 8%);
        box-shadow: 0px -1px 0px color.scale($color, $lightness: -8%);
      }
    }

    // Animation
    & {
      transform: scale(0.9) translate(calc(-50% - 32px), -50%);
      opacity: 0;

      transition: {
        property: transform, opacity;
        duration: 0.3s;
        delay: 0.05s;
        timing-function: cubic-bezier(0.52, 0.02, 0.19, 1.02);
      }
    }

    .sweet-buttons,
    .sweet-content {
      opacity: 0;

      transition: {
        property: transform, opacity;
        duration: 0.3s;
        delay: 0.09s;
        timing-function: cubic-bezier(0.52, 0.02, 0.19, 1.02);
      }
    }

    .sweet-content {
      transform: translateY(-8px);
    }

    .sweet-buttons {
      transform: translateY(16px);
    }

    &.is-visible {
      transform: translate(-50%, -50%);
      opacity: 1;

      .sweet-buttons,
      .sweet-content {
        transform: none;
        opacity: 1;
      }
    }

    &.bounce {
      animation-name: bounce;
      animation-duration: 0.3s;
      animation-iteration-count: 2;
      animation-direction: alternate;
    }

    // Responsiveness
    @include mixins.media(desktop) {
      // Bouncing animation
      @keyframes bounce {
        0% {
          transform: scale(1) translate(-50%, -50%);
        }

        50% {
          transform: scale(1.02) translate(calc(-50% + 8px), -50%);
        }

        100% {
          transform: scale(1) translate(-50%, -50%);
        }
      }
    }

    &.is-mobile-fullscreen {

      @include mixins.media(mobile) {

        & {
          width: 100%;
          height: 100%;

          left: 0;
          top: 0;

          transform: scale(0.9);

          &.is-visible {
            transform: none;
          }
        }

        .sweet-buttons {
          @include mixins.border-box;

          position: absolute;
          bottom: 0;
          left: 0;

          width: 100%;
        }
      }
    }
  }
</style>
